import React, { useState, useRef, useEffect } from 'react';
import { Rocket } from 'lucide-react';

const ContactForm = () => {
 const [formData, setFormData] = useState({
 fullName: '',
 companyName: '',
 phone: '',
 email: '',
 serviceOfInterest: '',
 projectDescription: ''
 });
 const [status, setStatus] = useState('idle'); // idle, submitting, success, error
 const [formError, setFormError] = useState('');
 const [isDropdownOpen, setIsDropdownOpen] = useState(false);
 const dropdownRef = useRef(null);

 useEffect(() => {
 const handleClickOutside = (event) => {
 if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
 setIsDropdownOpen(false);
 }
 };
 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }, []);

 const [isIndia, setIsIndia] = useState(true);

 useEffect(() => {
   // Initial fast guess based on system clock
   try {
     const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
     setIsIndia(tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta');
   } catch(e) {}

   // Accurate IP-based check (overrides timezone when using a VPN)
   fetch('https://ipapi.co/json/')
     .then(res => res.json())
     .then(data => {
       if (data && data.country_code) {
         setIsIndia(data.country_code === 'IN');
       }
     })
     .catch(err => console.warn('Geolocation failed, falling back to timezone.'));
 }, []);

 const serviceOptions = isIndia 
   ? ['INR 2 Lakh - 5 Lakh', 'INR 5 Lakh - 7 Lakh', 'INR 7 Lakh - 10 Lakh', 'INR 10 Lakh - 15 Lakh', 'INR 15 Lakh +']
   : ['$2,500 - $5,000 USD', '$5,000 - $10,000 USD', '$10,000 - $25,000 USD', '$25,000 - $50,000 USD', '$50,000+ USD'];

 const handleSubmit = async (e) => {
 e.preventDefault();
 setFormError('');
 
 setStatus('submitting');
 try {
 // Grab UTMs and Session
 const sessionId = sessionStorage.getItem('log_session_id') || '';
 const searchParams = new URLSearchParams(window.location.search);
 const utm_source = searchParams.get('utm_source') || 'Direct/Organic';
 const utm_medium = searchParams.get('utm_medium') || '';
 const utm_campaign = searchParams.get('utm_campaign') || '';
 const analyticsUuid = localStorage.getItem('analytics_uuid') || '';

 const payload = {
 ...formData,
 sessionId,
 utm_source,
 utm_medium,
 utm_campaign,
 submissionUrl: window.location.href,
 analyticsUuid
 };

 const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/crm/leads/submit`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(payload)
 });

 if (response.ok) {
 setStatus('success');
 setFormData({ fullName: '', companyName: '', phone: '', email: '', serviceOfInterest: '', projectDescription: '' });
 
 if (typeof window !== 'undefined' && window.gtag) {
   window.gtag('event', 'conversion', {
     'send_to': 'AW-18340567545/rprvCMzXj9QcEPmzu6lE',
     'value': 1.0,
     'currency': 'INR'
   });
 }
 } else {
 setStatus('error');
 }
 } catch (error) {
 setStatus('error');
 console.error("Submission error:", error);
 }
 };

 if (status === 'success') {
 return (
 <div id="contact" className="w-full flex justify-center animate-fade-in">
 <div className="w-full max-w-[900px] mx-auto bg-transparent md:bg-[#050505] border-none md:border md:border-white/10 rounded-[1.5rem] md:rounded-[2rem] px-6 py-12 md:px-12 md:py-20 shadow-none md:shadow-2xl mt-4 md:mt-8 flex flex-col items-center text-center">
 <div className="w-20 h-20 bg-[#09472d]/20 rounded-full flex items-center justify-center mb-6">
 <Rocket className="text-[#00ffcc] w-10 h-10" />
 </div>
 <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Thanks for reaching out!</h3>
 <p className="text-zinc-400 text-sm md:text-base max-w-md mx-auto">
 Your inquiry has been successfully received. Someone from our team will review your details and contact you within 24 hours.
 </p>
 </div>
 </div>
 );
 }

 return (
 <div id="contact" className="w-full flex justify-center">
 <div className="w-full max-w-[900px] mx-auto bg-transparent md:bg-[#050505] border-none md:border md:border-white/10 rounded-[1.5rem] md:rounded-[2rem] px-0 py-2 md:px-8 md:py-8 shadow-none md:shadow-2xl mt-4">
 <form className="space-y-4" onSubmit={handleSubmit}>
 {/* Full Name & Company */}
 <div className="grid grid-cols-2 gap-3 md:gap-5">
 {/* Full Name */}
 <div className="hidden md:flex flex-col text-left space-y-1.5">
 <label className="text-white text-xs md:text-sm font-bold ml-1">Full Name*</label>
 <input 
 type="text" 
 placeholder="John Doe" 
 required 
 value={formData.fullName} 
 onChange={e => setFormData({...formData, fullName: e.target.value})} 
 className="w-full bg-[#1a1a1a] border-0 outline-none ring-0 rounded-xl px-4 h-[48px] text-white placeholder-zinc-500 focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all text-xs md:text-sm " 
 />
 </div>
 <div className="md:hidden relative">
 <input 
 type="text" 
 id="fullNameMobile"
 placeholder=" " 
 required 
 value={formData.fullName} 
 onChange={e => setFormData({...formData, fullName: e.target.value})} 
 className="block px-4 h-[48px] w-full text-xs md:text-sm text-white bg-[#1a1a1a] rounded-xl border-0 outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all peer " 
 />
 <label 
 htmlFor="fullNameMobile" 
 className="absolute text-xs md:text-sm text-zinc-500 duration-300 transform -translate-y-1/2 scale-75 top-0 z-10 origin-[0] left-3 bg-transparent px-0 peer-focus:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-focus:top-0 peer-focus:scale-75 peer-focus:-translate-y-1/2 peer-focus:bg-transparent peer-focus:px-1 pointer-events-none"
 >
 Full Name*
 </label>
 </div>

 {/* Company Name */}
 <div className="hidden md:flex flex-col text-left space-y-1.5">
 <label className="text-white text-xs md:text-sm font-bold ml-1">Company Name</label>
 <input 
 type="text" 
 placeholder="Acme Corp" 
 value={formData.companyName} 
 onChange={e => setFormData({...formData, companyName: e.target.value})} 
 className="w-full bg-[#1a1a1a] border-0 outline-none ring-0 rounded-xl px-4 h-[48px] text-white placeholder-zinc-500 focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all text-xs md:text-sm " 
 />
 </div>
 <div className="md:hidden relative">
 <input 
 type="text" 
 id="companyMobile"
 placeholder=" " 
 value={formData.companyName} 
 onChange={e => setFormData({...formData, companyName: e.target.value})} 
 className="block px-4 h-[48px] w-full text-xs md:text-sm text-white bg-[#1a1a1a] rounded-xl border-0 outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all peer " 
 />
 <label 
 htmlFor="companyMobile" 
 className="absolute text-xs md:text-sm text-zinc-500 duration-300 transform -translate-y-1/2 scale-75 top-0 z-10 origin-[0] left-3 bg-transparent px-0 peer-focus:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-focus:top-0 peer-focus:scale-75 peer-focus:-translate-y-1/2 peer-focus:bg-transparent peer-focus:px-1 pointer-events-none"
 >
 Company Name
 </label>
 </div>
 </div>
 
 {/* Email & Phone */}
 <div className="grid grid-cols-2 gap-3 md:gap-5">
 {/* Email */}
 <div className="hidden md:flex flex-col text-left space-y-1.5">
 <label className="text-white text-xs md:text-sm font-bold ml-1">Work Email*</label>
 <input 
 type="email" 
 placeholder="john@example.com" 
 required 
 value={formData.email} 
 onChange={e => setFormData({...formData, email: e.target.value})} 
 className="w-full bg-[#1a1a1a] border-0 outline-none ring-0 rounded-xl px-4 h-[48px] text-white placeholder-zinc-500 focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all text-xs md:text-sm " 
 />
 </div>
 <div className="md:hidden relative">
 <input 
 type="email" 
 id="emailMobile"
 placeholder=" " 
 required 
 value={formData.email} 
 onChange={e => setFormData({...formData, email: e.target.value})} 
 className="block px-4 h-[48px] w-full text-xs md:text-sm text-white bg-[#1a1a1a] rounded-xl border-0 outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all peer " 
 />
 <label 
 htmlFor="emailMobile" 
 className="absolute text-xs md:text-sm text-zinc-500 duration-300 transform -translate-y-1/2 scale-75 top-0 z-10 origin-[0] left-3 bg-transparent px-0 peer-focus:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-focus:top-0 peer-focus:scale-75 peer-focus:-translate-y-1/2 peer-focus:bg-transparent peer-focus:px-1 pointer-events-none"
 >
 Work Email*
 </label>
 </div>

 {/* Phone */}
 <div className="hidden md:flex flex-col text-left space-y-1.5">
 <label className="text-white text-xs md:text-sm font-bold ml-1">Phone Number</label>
 <input 
 type="tel" 
 placeholder="+1 (555) 000-0000" 
 value={formData.phone} 
 onChange={e => setFormData({...formData, phone: e.target.value})} 
 className="w-full bg-[#1a1a1a] border-0 outline-none ring-0 rounded-xl px-4 h-[48px] text-white placeholder-zinc-500 focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all text-xs md:text-sm " 
 />
 </div>
 <div className="md:hidden relative">
 <input 
 type="tel" 
 id="phoneMobile"
 placeholder=" " 
 value={formData.phone} 
 onChange={e => setFormData({...formData, phone: e.target.value})} 
 className="block px-4 h-[48px] w-full text-xs md:text-sm text-white bg-[#1a1a1a] rounded-xl border-0 outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all peer " 
 />
 <label 
 htmlFor="phoneMobile" 
 className="absolute text-xs md:text-sm text-zinc-500 duration-300 transform -translate-y-1/2 scale-75 top-0 z-10 origin-[0] left-3 bg-transparent px-0 peer-focus:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-focus:top-0 peer-focus:scale-75 peer-focus:-translate-y-1/2 peer-focus:bg-transparent peer-focus:px-1 pointer-events-none"
 >
 Phone Number
 </label>
 </div>
 </div>

 {/* Dropdown */}
 <div className="flex flex-col text-left space-y-1.5 md:space-y-1.5" ref={dropdownRef}>
 <label className="hidden md:block text-white text-xs md:text-sm font-bold ml-1">Budget (Optional)</label>
 <div className="relative">
 <div 
 onClick={() => setIsDropdownOpen(!isDropdownOpen)}
 className={`w-full bg-[#1a1a1a] border ${isDropdownOpen ? 'border-[#333]' : 'border-transparent'} rounded-xl px-4 h-[48px] text-white focus:outline-none focus:ring-0 transition-all text-base cursor-pointer flex justify-between items-center peer ${!formData.serviceOfInterest ? 'py-3.5 md:py-3.5' : 'py-3.5 md:py-3.5'}`}
 >
 <span className={formData.serviceOfInterest ? "text-white" : "text-zinc-500 md:text-zinc-500 text-transparent"}>
 {formData.serviceOfInterest || (
 <span className="hidden md:inline">Select your budget (Optional)</span>
 )}
 </span>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 text-zinc-400 ${isDropdownOpen ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6"/></svg>
 </div>
 
 {/* Floating Label for Dropdown (Mobile Only) */}
 <label 
 className={`md:hidden absolute text-xs md:text-sm duration-300 transform z-10 origin-[0] pointer-events-none ${formData.serviceOfInterest || isDropdownOpen ? "scale-75 top-0 -translate-y-1/2 left-3 bg-transparent px-1 " + (isDropdownOpen ? "text-white" : "text-zinc-500") : "scale-100 top-1/2 -translate-y-1/2 left-4 bg-transparent px-0 text-zinc-500"}`}
 >
 Budget (Optional)
 </label>
 
 {/* Dropdown Menu */}
 {isDropdownOpen && (
 <div className="absolute z-50 w-full mt-2 bg-[#1a1a1a] border border-[#333] rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto custom-scrollbar">
 {serviceOptions.map((option, idx) => (
 <div 
 key={idx}
 onClick={() => {
 setFormData({...formData, serviceOfInterest: option});
 setIsDropdownOpen(false);
 }}
 className="px-4 py-3.5 text-xs md:text-sm text-white/90 hover:text-white hover:bg-[#333] cursor-pointer transition-colors border-b border-white/5 last:border-b-0"
 >
 {option}
 </div>
 ))}
 </div>
 )}
 </div>
 </div>

 {/* Textarea */}
 <div className="flex flex-col text-left space-y-1.5 md:space-y-1.5 mt-1">
 <label className="hidden md:block text-white text-xs md:text-sm font-bold ml-1">Project Details*</label>
 <div className="relative">
 <textarea 
 id="messageMobile"
 placeholder=" " 
 required
 rows="4" 
 value={formData.projectDescription} 
 onChange={e => setFormData({...formData, projectDescription: e.target.value})} 
 className="block px-4 py-3 min-h-[90px] w-full text-xs md:text-sm text-white bg-[#1a1a1a] rounded-xl border-0 outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus:ring-0 focus:bg-[#222] transition-all peer resize-none " 
 />
 {/* Desktop static placeholder (absolutely positioned because native placeholder is a space) */}
 {!formData.projectDescription && (
 <div className="hidden md:block absolute top-5 left-4 text-xs md:text-sm text-zinc-500 pointer-events-none">
 What are you looking to build? Tell us about your idea, requirements, and goals.
 </div>
 )}
 
 <label 
 htmlFor="messageMobile" 
 className="md:hidden absolute text-xs md:text-sm text-zinc-500 duration-300 transform -translate-y-1/2 scale-75 top-0 z-10 origin-[0] left-3 bg-transparent px-0 peer-focus:text-white peer-placeholder-shown:scale-100 peer-placeholder-shown:top-5 peer-placeholder-shown:-translate-y-0 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-focus:top-0 peer-focus:scale-75 peer-focus:-translate-y-1/2 peer-focus:bg-transparent peer-focus:px-1 pointer-events-none"
 >
 Project Details*
 </label>
 </div>
 </div>

 <button 
 type="submit" 
 disabled={status === 'submitting'} 
 className="w-full py-3.5 bg-[#09472d] text-white rounded-xl font-bold text-xs md:text-sm hover:bg-[#0c6340] transition-colors shadow-lg flex items-center justify-center gap-2"
 >
 {status === 'submitting' ? 'Submitting...' : 'Submit'}
 </button>
 
 {formError && <p className="text-red-400 text-xs md:text-sm font-medium mt-1 text-center">{formError}</p>}
 {status === 'error' && <p className="text-red-400 text-xs md:text-sm font-medium mt-1 text-center">Failed to submit. Please try again.</p>}
 </form>
 </div>
 </div>
 );
};

export default ContactForm;
